import _ from 'lodash';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { Button, FormCheck, Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import toastr from '../../../toastr';
import Label from '../Label';
import Model from '../../model/ShopKeeper.model';

/* While similar to the ForcePartModal component, this component has enough of its
   own logic specific to default parts to warrant its own implementation.
*/

interface IProps {
  apply: (
    partNum: string,
    netPrice: number,
    listPrice: number,
    tradePrice: number,
    description: string,
    stockClass: string,
    priceSymbol: string,
    unpriced: boolean
  ) => void;
  container: Object;
  data: {
    partNum: string;
    description: string;
    stockClass: string;
    priceSymbol: string;
    listPrice: string;
    netPrice: string;
    tradePrice: string;
  };
  hide: Function;
  model: Model;
  show: boolean;
  unpricedOptionAvailable?: boolean;
  unpricedOptionChecked?: boolean;
}

@observer
class NonStockingPartModal extends Component<IProps, {}> {
  @observable applyButtonDisabled = true;
  @observable grossProfitPercentage = '';
  @observable unpriced = false;

  escalatorList = 1;
  escalatorTrade = 1;

  applyButtonRef: HTMLButtonElement;
  partNumInput: HTMLInputElement;
  descriptionInput: HTMLInputElement;
  stockClassInput: HTMLInputElement;
  priceSymbolInput: HTMLInputElement;
  listPriceInput: HTMLInputElement;
  markupOverrideInput: HTMLInputElement;
  netPriceInput: HTMLInputElement;
  sellPriceInput: HTMLInputElement;

  constructor(props: IProps) {
    super(props);

    this.escalatorList = parseFloat(props.model.sysParams.escalatorListPrice) || 1;
    this.escalatorTrade = parseFloat(props.model.sysParams.escalatorTradePrice) || 1;
    this.unpriced = !!(props.unpricedOptionAvailable && props.unpricedOptionChecked);
  }

  componentDidMount() {
    // Timeout required until RBS fixes their React 16 async slot issues
    setTimeout(() => {
      this.partNumInput.focus();
      this.partNumInput.setSelectionRange(0, this.partNumInput.value.length);
      this.setNonStockingPartModalState();
    }, 0);
  }

  apply() {
    let netPrice, listPrice, tradePrice: number;

    if (this.unpriced) {
      netPrice = 0;
      listPrice = 0;
      tradePrice = 0;
    } else {
      // This should only be clickable if net/sell/trade are properly set, check anyway.
      netPrice = parseFloat(this.netPriceInput.value);
      listPrice = parseFloat(this.listPriceInput.value);
      tradePrice = parseFloat(this.sellPriceInput.value); // trade == sell

      // The following 2 checks should never occur as long as the event handlers
      // properly verify data.  This is a fast double check in case a handler gets messed up.
      if (isNaN(netPrice) || isNaN(listPrice) || isNaN(tradePrice)) {
        toastr.error('Error', 'Invalid price detected.  Try again.');
        return;
      }
    }

    const description = this.descriptionInput.value.trim();
    let partNum = this.partNumInput.value.trim();
    let priceSymbol = this.priceSymbolInput.value.trim();
    let stockClass = this.stockClassInput.value.trim();

    if (stockClass === '') {
      stockClass = this.props.model.sysParams.nonStockingDefaultStockClass;
    }
    if (priceSymbol === '') {
      priceSymbol = this.props.model.sysParams.nonStockingDefaultPriceSymbol;
    }

    // @parts must have a numeric price symbol between 10 and maxPriceSymbol, inclusive
    const maxPriceSymbol = this.props.model.sysParams.maxPriceSymbol;
    const priceSymbolInt = parseInt(priceSymbol, 10);
    if (isNaN(priceSymbolInt) || priceSymbolInt < 10 || priceSymbolInt > maxPriceSymbol) {
      toastr.error('Error', `Price symbol must be between 10 and ${maxPriceSymbol}, inclusive.`);
      // toastr steals our focus without a timeout
      setTimeout(() => {
        this.priceSymbolInput.focus();
        this.priceSymbolInput.setSelectionRange(0, this.priceSymbolInput.value.length);
      }, 100);

      return;
    }

    partNum = partNum.toUpperCase();

    if (partNum.startsWith('@')) {
      partNum = partNum.slice(1);
    }

    if (partNum.startsWith(':')) {
      partNum = partNum.slice(1);
    }

    if (!/^[A-Za-z]:/.test(partNum)) {
      partNum = `${this.props.model.defaultDivision}:${partNum}`;
    }

    this.props.apply(partNum, netPrice, listPrice, tradePrice, description, stockClass, priceSymbol, this.unpriced);
  }

  overrideMarkup(advanceField: boolean) {
    const markup = parseInt(this.markupOverrideInput.value.trim(), 10);
    if (!isNaN(markup)) {
      this.markupOverrideInput.value = markup.toString(); // cleanup input if necessary

      // apply markups to list/sell values
      const parsedNetOverride = parseFloat(this.netPriceInput.value.trim());
      if (!isNaN(parsedNetOverride)) {
        this.listPriceInput.value = ((1 + (2 * markup) / 100) * parsedNetOverride).toFixed(2);
        this.sellPriceInput.value = ((1 + markup / 100) * parsedNetOverride).toFixed(2);
      }

      // otherwise junk entered for markup
    } else {
      this.markupOverrideInput.value = '';
    }

    this.setNonStockingPartModalState();

    if (advanceField) {
      this.listPriceInput.focus();
      this.listPriceInput.setSelectionRange(0, this.listPriceInput.value.length);
    }
  }

  overrideList(advanceField: boolean) {
    const listOverride = this.listPriceInput.value.trim();

    // User or markup system has already entered a value?  Make sure it's a valid X.XX format and move on to sell field.
    // In the case markup is entered, manually changing this field will dominate the markup calculation.
    if (listOverride !== '') {
      const parsedListOverride = parseFloat(listOverride);
      if (!isNaN(parsedListOverride)) {
        this.listPriceInput.value = parsedListOverride.toFixed(2);
        this.setNonStockingPartModalState();
        if (advanceField) {
          this.sellPriceInput.focus();
          this.sellPriceInput.setSelectionRange(0, this.sellPriceInput.value.length);
        }
        return;
      }
      // else user entered junk input ("asdf") and we apply the logic below
    }

    // Here we logically have a blank input (or invalid input)

    // The net field will be either a valid number or blank.  If net is a number,
    // apply the appropriate list escalator as determined on mount.
    const netOverride = parseFloat(this.netPriceInput.value.trim());
    if (!isNaN(netOverride)) {
      this.listPriceInput.value = (this.escalatorList * netOverride).toFixed(2);

      // Go ahead and escalate sell price as well if it's empty
      // This will automatially call setDefaultPartModalState()
      this.overrideSell(false);
    } else {
      // Otherwise clear out any possible junk input (i.e. "asdf")
      this.listPriceInput.value = '';
      this.setNonStockingPartModalState();
    }

    // If a markup HAS been specified (and net is set), discard the previous if/else field
    // calculation and apply the net+markup instead (again, only on blank/invalid list input).
    this.overrideMarkup(false);

    if (advanceField) {
      this.sellPriceInput.focus();
      this.sellPriceInput.setSelectionRange(0, this.sellPriceInput.value.length);
    }
  }

  overrideNet(advanceField: boolean) {
    const netOverride = this.netPriceInput.value.trim();
    const parsedNetOverride = parseFloat(netOverride);

    if (isNaN(parsedNetOverride)) {
      this.netPriceInput.value = '';
    } else {
      this.netPriceInput.value = parsedNetOverride.toFixed(2);
    }

    // Apply any existing markup, if present
    this.overrideMarkup(false);

    this.setNonStockingPartModalState();

    if (advanceField) {
      this.markupOverrideInput.focus();
      this.markupOverrideInput.setSelectionRange(0, this.markupOverrideInput.value.length);
    }
  }

  overrideSell(advanceField: boolean) {
    const sellOverride = this.sellPriceInput.value.trim();

    // User or markup system has already entered a value?  Make sure it's a valid X.XX format and move on to apply button.
    // In the case markup is entered, manually changing this field will dominate the markup calculation.
    if (sellOverride !== '') {
      const parsedSellOverride = parseFloat(sellOverride);
      if (!isNaN(parsedSellOverride)) {
        this.sellPriceInput.value = parsedSellOverride.toFixed(2);
        this.setNonStockingPartModalState();
        if (advanceField) {
          // Give Apply button time to enable
          setTimeout(() => {
            (ReactDOM.findDOMNode(this.applyButtonRef) as HTMLButtonElement).focus();
          }, 0);
        }
        return;
      }
      // else user entered junk input ("asdf") and we apply the logic below
    }

    // Here we logically have a blank input (or invalid input)

    // The net field will be either a valid number or blank.  If net is a number,
    // apply the appropriate list escalator as determined on mount.
    const netOverride = parseFloat(this.netPriceInput.value.trim());
    if (!isNaN(netOverride)) {
      this.sellPriceInput.value = (this.escalatorTrade * netOverride).toFixed(2);
    } else {
      // Otherwise clear out any possible junk input (i.e. "asdf")
      this.sellPriceInput.value = '';
    }

    this.setNonStockingPartModalState();

    // If a markup HAS been specified (and net is set), discard the previous if/else field
    // calculation and apply the net+markup instead (again, only on blank/invalid sell input).
    this.overrideMarkup(false);

    if (advanceField) {
      // Give Apply button time to enable
      setTimeout(() => (ReactDOM.findDOMNode(this.applyButtonRef) as HTMLButtonElement).focus(), 0);
    }
  }

  // Determines if the "Apply" button should be active (all req fields present)
  setNonStockingPartModalState() {
    if (
      (this.netPriceInput.value !== '' &&
        this.listPriceInput.value !== '' &&
        this.sellPriceInput.value !== '' &&
        this.partNumInput.value !== '' &&
        this.partNumInput.value !== '@' &&
        this.partNumInput.value !== '$') ||
      (this.unpriced && this.partNumInput.value !== '' && this.partNumInput.value !== '$')
    ) {
      this.applyButtonDisabled = false;

      // Calculate gross profit %
      // This should only run if net/sell/trade are properly set, check anyway.
      const netPriceForce = parseFloat(this.netPriceInput.value);
      const listPriceForce = parseFloat(this.listPriceInput.value);
      const tradePriceForce = parseFloat(this.sellPriceInput.value); // trade == sell

      if (!(isNaN(netPriceForce) || isNaN(listPriceForce) || isNaN(tradePriceForce)) && tradePriceForce !== 0) {
        this.grossProfitPercentage = Math.trunc(((tradePriceForce - netPriceForce) / tradePriceForce) * 100).toFixed(0);
      } else {
        this.grossProfitPercentage = '';
      }
    } else {
      this.applyButtonDisabled = true;
      this.grossProfitPercentage = '';
    }
  }

  render() {
    // Popover to explain multi-use list input (dollar or % markup, analogous to PIN1)
    const listPricePopover = (
      <Popover id="modal-popover">
        <Popover.Title>% MARKUP HELP</Popover.Title>
        <Popover.Content>
          % of markup will apply to <strong>SELL</strong> price.
          <br />
          2*% of markup will apply to <strong>LIST</strong> price.
          <br />
          <em>Leave blank to use pricing escalators.</em>
        </Popover.Content>
      </Popover>
    );

    const unpricedPartPopover = (
      <Popover id="modal-popover">
        <Popover.Title>UNPRICED HELP</Popover.Title>
        <Popover.Content>
          <Popover.Content>
            If a part is issued unpriced, the repair order will be unable to be closed until an adjustment is made and a
            price is set for the part.
          </Popover.Content>
        </Popover.Content>
      </Popover>
    );

    return (
      <Modal
        backdrop="static"
        container={this.props.container as any}
        dialogClassName="sk-nonStockingPartModal"
        onHide={() => this.props.hide()}
        show={this.props.show}
      >
        <Modal.Header closeButton={true}>
          <Modal.Title>Non-Tracked Part Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Label>NON-TRACKED PART NUMBER</Label>
            <input
              defaultValue={this.props.data.partNum}
              onBlur={() => this.setNonStockingPartModalState()}
              onKeyPress={evt => {
                if (evt.key === 'Enter') {
                  this.setNonStockingPartModalState();
                  this.descriptionInput.focus();
                }
              }}
              ref={(ref: HTMLInputElement) => (this.partNumInput = ref)}
              type="text"
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ marginTop: '15px' }}>
            <Label>DESCRIPTION</Label>
            <input
              defaultValue={this.props.data.description}
              onKeyPress={evt => {
                if (evt.key === 'Enter') {
                  this.stockClassInput.focus();
                }
              }}
              ref={(ref: HTMLInputElement) => (this.descriptionInput = ref)}
              type="text"
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ marginTop: '15px' }}>
            <Label>STOCK CLASS</Label>
            <input
              defaultValue={this.props.data.stockClass}
              onKeyPress={evt => {
                if (evt.key === 'Enter') {
                  this.priceSymbolInput.focus();
                }
              }}
              ref={(ref: HTMLInputElement) => (this.stockClassInput = ref)}
              type="text"
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ marginTop: '15px' }}>
            <Label>PRICE SYMBOL</Label>
            <input
              defaultValue={this.props.data.priceSymbol}
              onKeyPress={evt => {
                if (evt.key === 'Enter') {
                  this.netPriceInput.focus();
                }
              }}
              ref={(ref: HTMLInputElement) => (this.priceSymbolInput = ref)}
              type="text"
              style={{ width: '100%' }}
            />
          </div>

          <hr style={{ marginBottom: '15px', marginTop: '20px' }} />

          <div className="sk-pricingControls">
            <div>
              <Label>NET</Label>
              <input
                defaultValue={this.props.data.netPrice}
                disabled={this.unpriced}
                onBlur={() => this.overrideNet(false)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    this.overrideNet(true);
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.netPriceInput = ref)}
                type="text"
              />
              <div className="sk-currentPrice">{this.props.data.netPrice}</div>
            </div>
            <div>
              <Label>% MARKUP</Label>
              <input
                disabled={this.unpriced}
                onBlur={() => this.overrideMarkup(false)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    this.overrideMarkup(true);
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.markupOverrideInput = ref)}
                type="text"
              />
              <br />
              <OverlayTrigger overlay={listPricePopover}>
                <div className="sk-listPriceOverrideHelp">
                  <i className="zmdi zmdi-help" />
                </div>
              </OverlayTrigger>
            </div>
            <div>
              <Label>LIST</Label>
              <input
                defaultValue={this.props.data.listPrice}
                disabled={this.unpriced}
                onBlur={() => this.overrideList(false)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    this.overrideList(true);
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.listPriceInput = ref)}
                type="text"
              />
              <div className="sk-currentPrice">{this.props.data.listPrice}</div>
            </div>
            <div>
              <Label>SELL</Label>
              <input
                defaultValue={this.props.data.tradePrice}
                disabled={this.unpriced}
                onBlur={() => this.overrideSell(false)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    this.overrideSell(true);
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.sellPriceInput = ref)}
                type="text"
              />
              <div className="sk-currentPrice">{this.props.data.tradePrice}</div>
            </div>
          </div>
          {this.props.model.sysParams.displayGrossProfitOnInquiry &&
            this.grossProfitPercentage !== '' &&
            !this.unpriced && (
              <div className="sk-grossProfitPercentage">Gross Profit: {this.grossProfitPercentage}%</div>
            )}
          {this.props.unpricedOptionAvailable && (
            <>
              <hr style={{ marginBottom: '15px', marginTop: '20px' }} />
              <div>
                <FormCheck inline={true}>
                  <FormCheck.Input
                    type="checkbox"
                    defaultChecked={this.props.unpricedOptionChecked}
                    onChange={(evt: any) => {
                      this.unpriced = evt.target.checked;
                      this.setNonStockingPartModalState();
                    }}
                    id="unpriced"
                    className="sk-useMaterialInput ml-1"
                    tabIndex={-1}
                  />
                  <FormCheck.Label htmlFor="unpriced">
                    Unpriced Part
                    <OverlayTrigger overlay={unpricedPartPopover}>
                      <span style={{ marginLeft: '15px' }}>
                        <i className="zmdi zmdi-help" />
                      </span>
                    </OverlayTrigger>
                  </FormCheck.Label>
                </FormCheck>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => this.props.hide()} variant="secondary">
            Cancel
          </Button>
          <Button
            variant="success"
            disabled={this.applyButtonDisabled}
            onClick={() => this.apply()}
            ref={(ref: HTMLButtonElement) => (this.applyButtonRef = ref)}
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default NonStockingPartModal;
